import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  FormLabel,
  FormHelperText,
  Typography,
  useMediaQuery,
  Theme,
  useTheme,
  List,
  ListItem,
  AlertTitle,
  Alert,
  CircularProgress,
  IconButton,
  Button,
} from '@material-ui/core';

import isEqual from 'lodash/isEqual';

import uploadFiles from './uploadFiles';
import { Delete } from '@material-ui/icons';
import { transformUploadData } from '../../utils';
import { Media } from '../../types';
import { ThumbnailPreview } from '../ThumbnailPreview';
import { FilePath, URN } from '@askporter/grieg-types';
import { FilePreview } from '..';

interface MultiFileUploadProps {
  id: string;
  label?: string;
  onChange?: (uploadedFiles: Array<Media>) => void;
  value?: Array<Media>;
  required?: boolean;
  disabled?: boolean;
}

const MultiFileUpload = ({
  id,
  label,
  onChange = () => ({}),
  value,
  required = false,
  disabled = false,
}: MultiFileUploadProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [isLoading, setIsLoading] = useState(false);
  const [filesSuccessfullyUploaded, setFilesSuccessfullyUploaded] = useState<Array<Media>>(value || []);
  const [filesErrored, setFilesErrored] = useState<Array<{ uid?: string; name: string; error?: unknown }>>([]);
  const [imagePreview, setImagePreview] = useState<FilePath | undefined>();

  const onDrop = useCallback(
    async (acceptedFiles) => {
      setIsLoading(true);
      const uploadResults = await uploadFiles(acceptedFiles);
      const succesfullyUploadedFiles = uploadResults
        .filter(({ uid, error }) => uid && !error)
        .map((d) => transformUploadData(d));
      setFilesSuccessfullyUploaded([...filesSuccessfullyUploaded, ...succesfullyUploadedFiles]);
      setFilesErrored([...uploadResults.filter(({ uid, error }) => !uid || !!error)]);
      setIsLoading(false);
    },
    [filesSuccessfullyUploaded],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  useEffect(() => {
    onChange(filesSuccessfullyUploaded);
  }, [filesSuccessfullyUploaded, onChange]);

  useEffect(() => {
    if (!isEqual(value, filesSuccessfullyUploaded)) {
      setFilesSuccessfullyUploaded(value || []);
    }
  }, [value]);

  const handleFileRemove = (urn: URN) => {
    setFilesSuccessfullyUploaded(filesSuccessfullyUploaded.filter(({ file }) => file.fileUrn !== urn));
  };

  return (
    <>
      <FormControl required={required} fullWidth data-testid={`multi-file-upload--${id}`}>
        <FormLabel htmlFor={`multi-file-input--${id}`}>{label || t('ns.common:file_upload:label')}</FormLabel>
        {!disabled && <FormHelperText>{t('ns.common:file_upload:help_text')}</FormHelperText>}

        {filesErrored.length > 0 && (
          <Alert severity="error" variant="outlined" sx={{ marginTop: 4 }} data-testid="files-errored">
            <AlertTitle>{t('ns.common:file_upload:files_errored')}</AlertTitle>
            <List>
              {filesErrored.map((file) => {
                return (
                  <ListItem key={JSON.stringify(file.error)}>
                    <Typography variant="subtitle2">{file.name}</Typography>
                  </ListItem>
                );
              })}
            </List>
          </Alert>
        )}

        {filesSuccessfullyUploaded.length > 0 && (
          <List sx={{ border: `1px solid ${theme.palette.grey['400']}`, marginTop: 4 }} data-testid="uploaded-files">
            {filesSuccessfullyUploaded.map((file) => {
              const fileName = `${file.nameAndType?.name}.${file.nameAndType?.type}`;
              return (
                <ListItem
                  key={`${file.file.fileUrn}`}
                  secondaryAction={
                    !disabled && (
                      <IconButton aria-label={`remove ${fileName}`} onClick={() => handleFileRemove(file.file.fileUrn)}>
                        <Delete />
                      </IconButton>
                    )
                  }
                >
                  <Button
                    variant="unstyled"
                    onClick={() => setImagePreview({ fileUrn: file.file.fileUrn, filePath: file.path })}
                  >
                    <ThumbnailPreview filePath={file.path} imgAlt={label} />
                  </Button>
                  <Box sx={{ ml: 2 }}>{fileName}</Box>
                </ListItem>
              );
            })}
          </List>
        )}

        {isLoading && (
          <Box
            sx={{
              border: `1px dashed ${theme.palette.grey['500']}`,
              cursor: 'disabled',
              padding: isSmallDevice ? 4 : 24,
              textAlign: 'center',
              marginY: 4,
            }}
            data-testid="loading-indicator"
          >
            <CircularProgress />
          </Box>
        )}

        {!isLoading && !disabled && (
          <Box
            {...getRootProps()}
            sx={{
              border: `1px dashed ${theme.palette.grey['500']}`,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.7,
              },
              padding: isSmallDevice ? 4 : 24,
              marginY: 4,
            }}
          >
            <input disabled={disabled} id={`multi-file-input--${id}`} {...getInputProps()} />
            <Typography color={theme.palette.primary.main}>
              {isSmallDevice && t('ns.common:file_upload:small_device_input_placeholder')}
              {!isSmallDevice &&
                (isDragActive
                  ? t('ns.common:file_upload:input_placeholder_drag_active')
                  : t('ns.common:file_upload:input_placeholder'))}
            </Typography>
          </Box>
        )}
      </FormControl>
      {imagePreview && (
        <FilePreview isSmallDevice={isSmallDevice} handleClose={() => setImagePreview(undefined)} file={imagePreview} />
      )}
    </>
  );
};

export default MultiFileUpload;
